import { Injectable } from '@nestjs/common';
import {
  CreateProductRequest,
  ProductDetail,
  ProductListQuery,
  ProductStatsResponse,
  ProductSummary,
  UpdateProductRequest,
} from '@cart/shared-types';
import { PRODUCTS } from './products.data';

// Same threshold the admin product table already uses for its
// stock-low row styling (products-page.component.html's stock column).
export const LOW_STOCK_THRESHOLD = 10;

export abstract class ProductsRepository {
  abstract list(query: ProductListQuery): Promise<ProductSummary[]>;
  abstract count(query: ProductListQuery): Promise<number>;
  abstract stats(query: ProductListQuery): Promise<ProductStatsResponse>;
  abstract findBySlug(slug: string): Promise<ProductDetail | undefined>;
  abstract findById(productId: string): Promise<ProductSummary | undefined>;
  abstract findByIdFull(productId: string): Promise<ProductDetail | undefined>;
  abstract create(input: CreateProductRequest): Promise<ProductSummary>;
  abstract update(productId: string, input: UpdateProductRequest): Promise<ProductSummary | undefined>;
  abstract updateStock(productId: string, stock: number): Promise<ProductSummary | undefined>;
  abstract deactivate(productId: string): Promise<ProductSummary | undefined>;
  abstract existsBySlug(slug: string): Promise<boolean>;

  /**
   * Active variants on a product, used to refuse a product-level stock write
   * when the variants own the stock. Optional because the in-memory repository
   * has no variants at all — absent reads as zero, which is correct for it.
   */
  countActiveVariants?(productId: string): Promise<number>;
}

@Injectable()
export class InMemoryProductsRepository implements ProductsRepository {
  private readonly products: ProductSummary[] = PRODUCTS.map((product) => ({ ...product }));

  private filterProducts(query: ProductListQuery): ProductSummary[] {
    const search = query.search?.trim().toLowerCase();
    const category = query.category?.trim().toLowerCase();

    return this.products
      .filter((product) => query.includeInactive || product.active)
      .filter((product) => !search || product.name.toLowerCase().includes(search))
      .filter((product) => !category || product.category === category)
      .filter((product) => query.minPrice === undefined || product.price >= query.minPrice)
      .filter((product) => query.maxPrice === undefined || product.price <= query.maxPrice)
      .filter(
        (product) =>
          query.inStock === undefined ||
          (query.inStock ? product.stock > 0 : product.stock === 0)
      )
      .filter((product) => !query.categoryIds?.length || (product.categoryId !== undefined && query.categoryIds.includes(product.categoryId)))
      .filter((product) => query.taxMode === undefined || product.taxMode === query.taxMode)
      .filter((product) => query.taxProfileId === undefined || product.taxProfileId === query.taxProfileId)
      // Simplified vs. the Prisma repo: no slab-profile data to resolve a
      // predefined product's rate in this in-memory fixture, so this only
      // matches custom-mode products carrying that exact rate directly.
      .filter((product) => query.taxRateBps === undefined || product.customIgstRateBps === query.taxRateBps)
      .filter((product) => query.taxConfigStatus !== 'missing' || (product.taxMode === 'predefined' && !product.taxProfileId))
      .filter((product) => query.brandId === undefined || product.brandId === query.brandId)
      .filter((product) => this.matchesStatus(product, query.status))
      .sort((left, right) => this.sortProducts(left, right, query.sort));
  }

  private matchesStatus(product: ProductSummary, status: ProductListQuery['status']): boolean {
    if (status === undefined) return true;
    if (status === 'active') return product.active;
    if (status === 'draft') return !product.active;
    if (status === 'out_of_stock') return product.stock === 0;
    return product.stock > 0 && product.stock <= LOW_STOCK_THRESHOLD; // 'low_stock'
  }

  async list(query: ProductListQuery): Promise<ProductSummary[]> {
    const page = Math.max(1, query.page ?? 1);
    const limit = Math.min(100, Math.max(1, query.limit ?? 20));
    const skip = (page - 1) * limit;

    return this.filterProducts(query).slice(skip, skip + limit);
  }

  async count(query: ProductListQuery): Promise<number> {
    return this.filterProducts(query).length;
  }

  async stats(query: ProductListQuery): Promise<ProductStatsResponse> {
    // Stats reflect the whole catalog under the current search/category/
    // price filters — everything except `status` itself, so the 6 cards
    // stay meaningful no matter which status bucket is currently selected.
    const { status: _status, ...base } = query;
    const scoped = this.filterProducts({ ...base, includeInactive: true });

    return {
      total: scoped.length,
      active: scoped.filter((p) => p.active).length,
      draft: scoped.filter((p) => !p.active).length,
      outOfStock: scoped.filter((p) => p.stock === 0).length,
      lowStock: scoped.filter((p) => p.stock > 0 && p.stock <= LOW_STOCK_THRESHOLD).length,
      archived: 0,
    };
  }

  async create(input: CreateProductRequest): Promise<ProductSummary> {
    const product: ProductSummary = {
      id: `prod-${String(this.products.length + 1).padStart(3, '0')}`,
      name: input.name.trim(),
      slug: input.slug.trim(),
      category: input.category.trim().toLowerCase(),
      price: input.price,
      discount: input.discount,
      paymentMethods: input.paymentMethods,
      imageUrl: input.imageUrl?.trim() ?? '',
      mediaSizeBytes: 0,
      stock: input.stock,
      active: input.active ?? true,
      createdAt: new Date().toISOString(),
      ...(input.sku?.trim() ? { sku: input.sku.trim() } : {}),
      ...(input.barcode?.trim() ? { barcode: input.barcode.trim() } : {}),
    };

    this.products.push(product);

    return product;
  }

  async findBySlug(slug: string): Promise<ProductDetail | undefined> {
    const product = this.products.find((item) => item.slug === slug && item.active);

    if (!product) {
      return undefined;
    }

    return {
      ...product,
      description: `${product.name} is ready for everyday use with current stock and pricing from the catalog.`,
      images: [{ url: product.imageUrl, altText: product.name }],
      specs: [],
    };
  }

  async findById(productId: string): Promise<ProductSummary | undefined> {
    const product = this.products.find((item) => item.id === productId);

    return product ? { ...product } : undefined;
  }

  async findByIdFull(productId: string): Promise<ProductDetail | undefined> {
    const product = this.products.find((item) => item.id === productId);

    if (!product) return undefined;

    return {
      ...product,
      description: `${product.name} is ready for everyday use with current stock and pricing from the catalog.`,
      images: product.imageUrl ? [{ url: product.imageUrl, altText: product.name }] : [],
      specs: [],
    };
  }

  async update(productId: string, input: UpdateProductRequest): Promise<ProductSummary | undefined> {
    const product = this.products.find((item) => item.id === productId);

    if (!product) {
      return undefined;
    }

    if (input.name !== undefined) product.name = input.name.trim();
    if (input.price !== undefined) product.price = input.price;
    if (input.discount !== undefined) product.discount = input.discount;
    if (input.paymentMethods !== undefined) product.paymentMethods = input.paymentMethods;
    if (input.active !== undefined) product.active = input.active;
    if (input.categorySlug !== undefined) product.category = input.categorySlug.trim().toLowerCase();
    if (input.categoryId !== undefined) product.category = input.categoryId;
    if (input.sku !== undefined) product.sku = input.sku.trim() || undefined;
    if (input.barcode !== undefined) product.barcode = input.barcode.trim() || undefined;

    return { ...product };
  }

  async updateStock(productId: string, stock: number): Promise<ProductSummary | undefined> {
    const product = this.products.find((item) => item.id === productId);

    if (!product) {
      return undefined;
    }

    product.stock = stock;

    return { ...product };
  }

  async deactivate(productId: string): Promise<ProductSummary | undefined> {
    const product = this.products.find((item) => item.id === productId);

    if (!product) {
      return undefined;
    }

    product.active = false;

    return { ...product };
  }

  async existsBySlug(slug: string): Promise<boolean> {
    return this.products.some((product) => product.slug === slug);
  }

  private sortProducts(
    left: ProductSummary,
    right: ProductSummary,
    sort: ProductListQuery['sort'] = 'newest'
  ): number {
    if (sort === 'price-asc') return left.price - right.price || left.name.localeCompare(right.name);
    if (sort === 'price-desc') return right.price - left.price || left.name.localeCompare(right.name);
    if (sort === 'name-asc') return left.name.localeCompare(right.name);
    if (sort === 'name-desc') return right.name.localeCompare(left.name);
    if (sort === 'sku-asc') return (left.sku ?? '').localeCompare(right.sku ?? '');
    if (sort === 'sku-desc') return (right.sku ?? '').localeCompare(left.sku ?? '');
    if (sort === 'category-asc') return left.category.localeCompare(right.category);
    if (sort === 'category-desc') return right.category.localeCompare(left.category);
    if (sort === 'updated-asc') {
      return Date.parse(left.updatedAt ?? left.createdAt) - Date.parse(right.updatedAt ?? right.createdAt);
    }
    if (sort === 'updated-desc') {
      return Date.parse(right.updatedAt ?? right.createdAt) - Date.parse(left.updatedAt ?? left.createdAt);
    }

    return Date.parse(right.createdAt) - Date.parse(left.createdAt);
  }
}
